import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useBranch } from '../contexts/BranchContext';
import { useAuth } from '../App';
import api, { getErrorMessage } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Receipt, FileXls, ShoppingCart, Trash, Plus, Printer, Download, CaretDown, CaretUp, Pencil, Package } from '@phosphor-icons/react';
import BarcodeScanButton from '../components/BarcodeScanButton';

const SalesRecords = () => {
  const { activeBranch } = useBranch();
  const { user } = useAuth();
  const userRole = user?.role || 'staff';
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Separate date filter states for Sales and Collections
  const [salesFromDate, setSalesFromDate] = useState('');
  const [salesToDate, setSalesToDate] = useState('');
  const [collectionFromDate, setCollectionFromDate] = useState('');
  const [collectionToDate, setCollectionToDate] = useState('');
  const [showOutstandingList, setShowOutstandingList] = useState(false);
  
  const [showSaleDialog, setShowSaleDialog] = useState(false);
  const [showSalePaymentDialog, setShowSalePaymentDialog] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDeliverDialog, setShowDeliverDialog] = useState(false);
  const [saleToDeliver, setSaleToDeliver] = useState(null);
  
  const [selectedSale, setSelectedSale] = useState(null);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [calculatedBalance, setCalculatedBalance] = useState(0); // Dynamically calculated balance
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [expandedSale, setExpandedSale] = useState(null);
  const [editingSale, setEditingSale] = useState(null); // Sale being edited
  const [isEditMode, setIsEditMode] = useState(false); // Track if we're in edit mode
  const [editingPayment, setEditingPayment] = useState(null); // Payment being edited
  const [showPaymentEditDialog, setShowPaymentEditDialog] = useState(false);
  const [saleDateEdit, setSaleDateEdit] = useState(''); // For editing invoice date
  
  const [saleCart, setSaleCart] = useState([]);
  const [saleCustomer, setSaleCustomer] = useState('');
  const [salePayment, setSalePayment] = useState({
    amount_paid: 0,
    payment_method: 'cash'
  });
  const [currentItem, setCurrentItem] = useState({
    barcode: '',
    quantity: 1,
    price_per_unit: 0
  });
  
  const [salePaymentForm, setSalePaymentForm] = useState({
    amount: 0,
    payment_method: 'cash',
    remarks: ''
  });

  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Cash' },
    { value: 'online', label: 'Online Transfer' },
    { value: 'upi', label: 'UPI' },
    { value: 'check', label: 'Cheque' },
    { value: 'card', label: 'Card' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (activeBranch) {
      fetchSalesData();
      fetchCustomers();
    }
  }, [activeBranch, salesFromDate, salesToDate]);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers', {
        params: { branch_id: activeBranch?.branch_id }
      });
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const fetchSalesData = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const params = { branch_id: activeBranch.branch_id };
      // Use sales date filter for fetching
      if (salesFromDate) params.from_date = salesFromDate;
      if (salesToDate) params.to_date = salesToDate;
      
      // Add timestamp to force fresh data after edits
      if (forceRefresh) {
        params._t = Date.now();
      }
      
      const response = await api.get('/ledger/sales', { params });
      setSales(response.data);
    } catch (error) {
      console.error('Sales data fetch error:', error);
      if (error.response?.status === 403) {
        toast.error('Access denied to Sales Records');
      } else {
        toast.error(getErrorMessage(error, 'Failed to load sales data'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!currentItem.barcode) {
      toast.error('Please enter a barcode');
      return;
    }
    
    try {
      const response = await api.get(`/stock/barcode/${currentItem.barcode}`);
      const item = response.data;
      
      const existingIndex = saleCart.findIndex(i => i.barcode === currentItem.barcode);
      if (existingIndex >= 0) {
        const updated = [...saleCart];
        updated[existingIndex].quantity += currentItem.quantity;
        updated[existingIndex].subtotal = updated[existingIndex].quantity * updated[existingIndex].price_per_unit;
        setSaleCart(updated);
      } else {
        setSaleCart([...saleCart, {
          barcode: item.barcode,
          item_name: item.name,
          quantity: currentItem.quantity,
          price_per_unit: currentItem.price_per_unit,
          subtotal: currentItem.quantity * currentItem.price_per_unit
        }]);
      }
      
      setCurrentItem({ barcode: '', quantity: 1, price_per_unit: 0 });
      toast.success(`${item.name} added to cart`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Item not found'));
    }
  };

  const handleRemoveFromCart = (index) => {
    setSaleCart(saleCart.filter((_, i) => i !== index));
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    
    if (saleCart.length === 0) {
      toast.error('Please add at least one item to cart');
      return;
    }
    
    try {
      const payload = {
        branch_id: activeBranch.branch_id,
        line_items: saleCart.map(item => ({
          barcode: item.barcode,
          quantity: item.quantity,
          price_per_unit: item.price_per_unit
        })),
        customer_id: saleCustomer || undefined,
        amount_paid: salePayment.amount_paid,
        payment_method: salePayment.payment_method,
        sale_date: saleDateEdit || undefined  // Include invoice date
      };
      
      let response;
      if (isEditMode && editingSale) {
        // Update existing sale
        response = await api.put(`/ledger/sale/${editingSale.sale_id}`, payload);
        toast.success('Sale updated successfully!');
      } else {
        // Create new sale
        response = await api.post('/ledger/sale', payload);
        toast.success('Sale recorded successfully! Invoice generated.');
      }
      
      setCurrentInvoice(response.data);
      setShowSaleDialog(false);
      setShowInvoiceModal(true);
      
      // Reset form
      setSaleCart([]);
      setSaleCustomer('');
      setSalePayment({ amount_paid: 0, payment_method: 'cash' });
      setCurrentItem({ barcode: '', quantity: 1, price_per_unit: 0 });
      setIsEditMode(false);
      setEditingSale(null);
      setSaleDateEdit(''); // Reset date
      
      // Force refresh with delay to ensure backend updates are complete
      setTimeout(() => fetchSalesData(true), 300);
    } catch (error) {
      toast.error(getErrorMessage(error, isEditMode ? 'Update failed' : 'Sale failed'));
    }
  };
  
  const openPaymentEdit = (sale, payment, paymentIndex) => {
    setEditingPayment({
      sale_id: sale.sale_id,
      invoice_number: sale.invoice_number,
      paymentIndex: paymentIndex,
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_method: payment.payment_method,
      remarks: payment.remarks || ''
    });
    setShowPaymentEditDialog(true);
  };
  
  const handlePaymentEdit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await api.put(
        `/ledger/sale/${editingPayment.sale_id}/payment/${editingPayment.paymentIndex}`,
        null,
        {
          params: {
            payment_date: editingPayment.payment_date,
            amount: editingPayment.amount,
            payment_method: editingPayment.payment_method,
            remarks: editingPayment.remarks
          }
        }
      );
      
      toast.success('Payment history updated successfully!');
      setShowPaymentEditDialog(false);
      setEditingPayment(null);
      
      // Force refresh with delay to ensure backend updates are complete
      setTimeout(() => fetchSalesData(true), 300);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update payment'));
    }
  };
  
  const openEditSale = async (sale) => {
    try {
      // Fetch full sale details
      const response = await api.get(`/ledger/sale/${sale.sale_id}/invoice`);
      const saleData = response.data;
      
      // Check if it's a legacy record (no line_items array)
      const isLegacy = !saleData.line_items || saleData.line_items.length === 0;
      
      if (isLegacy) {
        toast.error('Legacy sales records cannot be edited. Please create a new sale instead.');
        return;
      }
      
      // Set edit mode
      setIsEditMode(true);
      setEditingSale(saleData);
      
      // Pre-fill cart with existing items
      const cartItems = saleData.line_items.map(item => ({
        barcode: item.barcode,
        item_name: item.item_name,
        quantity: item.quantity,
        price_per_unit: item.price_per_unit,
        unit: item.unit,
        subtotal: item.subtotal
      }));
      setSaleCart(cartItems);
      
      // Pre-fill customer and payment
      setSaleCustomer(saleData.customer_id || '');
      
      // Calculate total paid from payment_history for consistency
      const paymentHistory = saleData.payment_history || [];
      const totalPaid = paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0);
      
      setSalePayment({
        amount_paid: totalPaid,
        payment_method: saleData.payment_method || paymentHistory[0]?.payment_method || 'cash'
      });
      
      // Pre-fill sale date
      setSaleDateEdit(saleData.sale_date || '');
      
      // Open dialog
      setShowSaleDialog(true);
    } catch (error) {
      toast.error('Failed to load sale details for editing');
    }
  };

  const viewInvoice = async (saleId) => {
    try {
      const response = await api.get(`/ledger/sale/${saleId}/invoice`);
      const invoiceData = response.data;
      // Calculate actual balance from payment_history
      const actualBalance = calculateActualBalance(invoiceData);
      // Attach calculated balance to invoice
      invoiceData.calculated_balance = actualBalance;
      setCurrentInvoice(invoiceData);
      setShowInvoiceModal(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const printInvoice = () => {
    window.print();
  };

  const downloadInvoice = () => {
    const invoiceContent = document.getElementById('invoice-content');
    if (invoiceContent) {
      const printWindow = window.open('', '', 'width=800,height=600');
      printWindow.document.write('<html><head><title>Invoice</title>');
      printWindow.document.write('<style>body{font-family:Arial,sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th,td{border:1px solid #ddd;padding:8px;text-align:left;}</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(invoiceContent.innerHTML);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.print();
    }
  };

  const getCartTotal = () => {
    return saleCart.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const toggleSalePaymentHistory = (saleId) => {
    setExpandedSale(expandedSale === saleId ? null : saleId);
  };

  // Helper function to calculate actual remaining balance from payment_history
  const calculateActualBalance = (sale) => {
    const saleAmount = sale.total_amount || sale.sale_amount || 0;
    const paymentHistory = sale.payment_history || [];
    const totalPaid = paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0);
    return Math.max(0, saleAmount - totalPaid);
  };

  const openSalePaymentDialog = (sale) => {
    setSelectedSale(sale);
    // Calculate actual balance from payment_history (not stale DB field)
    const actualBalance = calculateActualBalance(sale);
    setCalculatedBalance(actualBalance);
    setSalePaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
    setShowSalePaymentDialog(true);
  };

  const handleSalePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/ledger/sale/${selectedSale.sale_id}/payment`, salePaymentForm);
      toast.success('Payment collected successfully!');
      setShowSalePaymentDialog(false);
      setSalePaymentForm({ amount: 0, payment_method: 'cash', remarks: '' });
      setSelectedSale(null);
      fetchSalesData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Payment failed'));
    }
  };

  const handleDeleteSale = async () => {
    try {
      const response = await api.delete(`/ledger/sale/${saleToDelete.sale_id}`);
      if (response.data.stock_restored) {
        toast.success('Sale deleted and stock restored to inventory!');
      } else {
        toast.success('Sale deleted successfully!');
      }
      setShowDeleteDialog(false);
      setSaleToDelete(null);
      fetchSalesData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete sale'));
    }
  };

  const openDeleteDialog = (sale) => {
    setSaleToDelete(sale);
    setShowDeleteDialog(true);
  };

  const openDeliverDialog = (sale) => {
    setSaleToDeliver(sale);
    setShowDeliverDialog(true);
  };

  const handleDeliverSale = async () => {
    try {
      const response = await api.post(`/ledger/sale/${saleToDeliver.sale_id}/deliver`);
      toast.success('Items delivered successfully! Stock has been deducted from inventory.');
      setShowDeliverDialog(false);
      setSaleToDeliver(null);
      fetchSalesData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to mark as delivered'));
    }
  };

  const exportToExcel = (data, filename) => {
    if (data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(item => Object.values(item).map(val => `"${val}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  if (loading) {
    return (
      <Layout title="Sales Records">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
            <p className="mt-4 text-[#2C4C3B]">Loading sales data...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Sales Records">
      <div className="space-y-6">
        {/* Separate Date Filters */}
        <Card className="p-4 bg-white border-[#E5E5DF]">
          <div className="space-y-4">
            {/* Sales Date Filter */}
            <div>
              <h3 className="text-sm font-semibold text-[#2C4C3B] mb-2">📅 Sales Date Filter</h3>
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs text-[#1C1C1A]/70">From Date</Label>
                  <Input
                    type="date"
                    value={salesFromDate}
                    onChange={(e) => setSalesFromDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs text-[#1C1C1A]/70">To Date</Label>
                  <Input
                    type="date"
                    value={salesToDate}
                    onChange={(e) => setSalesToDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {(salesFromDate || salesToDate) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSalesFromDate('');
                      setSalesToDate('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            
            {/* Collection Date Filter */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-green-700 mb-2">💰 Collection Date Filter</h3>
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs text-[#1C1C1A]/70">From Date</Label>
                  <Input
                    type="date"
                    value={collectionFromDate}
                    onChange={(e) => setCollectionFromDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <Label className="text-xs text-[#1C1C1A]/70">To Date</Label>
                  <Input
                    type="date"
                    value={collectionToDate}
                    onChange={(e) => setCollectionToDate(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {(collectionFromDate || collectionToDate) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCollectionFromDate('');
                      setCollectionToDate('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-lg">Sales Records</h3>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => exportToExcel(sales, 'sales')}>
                  <FileXls size={16} className="mr-2" />
                  Export
                </Button>
                <Button size="sm" onClick={() => {
                  setIsEditMode(false);
                  setEditingSale(null);
                  setSaleCart([]);
                  setSaleCustomer('');
                  setSalePayment({ amount_paid: 0, payment_method: 'cash' });
                  setSaleDateEdit(''); // Reset date
                  setShowSaleDialog(true);
                }} className="bg-[#2C4C3B]">
                  <ShoppingCart size={16} className="mr-2" />
                  Record Sale
                </Button>
              </div>
            </div>

            {/* Sales Summary */}
            {sales.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Total Sales Card */}
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <p className="text-sm text-[#1C1C1A]/60 mb-1">
                    📊 Total Sales {(salesFromDate || salesToDate) && <span className="text-xs">(Filtered)</span>}
                  </p>
                  <p className="text-2xl font-bold text-[#2C4C3B]">
                    ₹{sales.reduce((sum, sale) => sum + (sale.total_amount || sale.sale_amount || 0), 0).toLocaleString()}
                  </p>
                </div>
                
                {/* Total Collected Card */}
                <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                  <p className="text-sm text-[#1C1C1A]/60 mb-1">
                    💰 Total Collected {(collectionFromDate || collectionToDate) ? <span className="text-xs">(Filtered)</span> : <span className="text-xs">(All Time)</span>}
                  </p>
                  <p className="text-2xl font-bold text-green-700">
                    ₹{sales.reduce((sum, sale) => {
                      // ALWAYS calculate from payment_history
                      const paymentHistory = sale.payment_history || [];
                      
                      // Filter payments by collection date filter
                      const relevantPayments = paymentHistory.filter(payment => {
                        const paymentDate = payment.payment_date;
                        if (!paymentDate) return false;
                        
                        // Use collection date filters
                        if (collectionFromDate && paymentDate < collectionFromDate) return false;
                        if (collectionToDate && paymentDate > collectionToDate) return false;
                        
                        return true;
                      });
                      
                      const collectedInRange = relevantPayments.reduce((total, p) => total + (p.amount || 0), 0);
                      return sum + collectedInRange;
                    }, 0).toLocaleString()}
                  </p>
                </div>
                
                {/* Total Balance Due Card - Always shows current outstanding */}
                <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200">
                  <p className="text-sm text-[#1C1C1A]/60 mb-1">
                    ⚠️ Total Balance Due <span className="text-xs">(All Outstanding)</span>
                  </p>
                  <p className="text-2xl font-bold text-orange-700">
                    ₹{sales.reduce((sum, sale) => {
                      const saleAmount = sale.total_amount || sale.sale_amount || 0;
                      const paymentHistory = sale.payment_history || [];
                      const totalPaid = paymentHistory.reduce((total, p) => total + (p.amount || 0), 0);
                      const balance = saleAmount - totalPaid;
                      return sum + Math.max(0, balance);
                    }, 0).toLocaleString()}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 text-xs"
                    onClick={() => setShowOutstandingList(!showOutstandingList)}
                  >
                    {showOutstandingList ? 'Hide' : 'View'} Customer List
                  </Button>
                </div>
              </div>
            )}
              
            {/* Outstanding Customers List */}
            {showOutstandingList && (
                <div className="mb-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <h3 className="text-sm font-semibold text-orange-800 mb-3">Customers with Outstanding Balance:</h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {sales
                      .map(sale => {
                        const saleAmount = sale.total_amount || sale.sale_amount || 0;
                        const paymentHistory = sale.payment_history || [];
                        const totalPaid = paymentHistory.reduce((total, p) => total + (p.amount || 0), 0);
                        const balance = saleAmount - totalPaid;
                        return { ...sale, balance: Math.max(0, balance) };
                      })
                      .filter(sale => sale.balance > 0)
                      .sort((a, b) => b.balance - a.balance)
                      .map((sale, index) => (
                        <div key={sale.sale_id} className="flex justify-between items-center p-2 bg-white rounded border border-orange-200 text-sm">
                          <div className="flex-1">
                            <span className="font-medium text-[#2C4C3B]">{index + 1}. {sale.customer_name || 'Walk-in'}</span>
                            <span className="text-xs text-[#1C1C1A]/60 ml-2">({sale.invoice_number || 'N/A'})</span>
                          </div>
                          <div className="text-right">
                            <Badge variant="destructive" className="font-semibold">₹{sale.balance.toLocaleString()}</Badge>
                            <div className="text-xs text-[#1C1C1A]/60 mt-1">{sale.sale_date}</div>
                          </div>
                        </div>
                      ))}
                    {sales.filter(sale => {
                      const saleAmount = sale.total_amount || sale.sale_amount || 0;
                      const paymentHistory = sale.payment_history || [];
                      const totalPaid = paymentHistory.reduce((total, p) => total + (p.amount || 0), 0);
                      return (saleAmount - totalPaid) > 0;
                    }).length === 0 && (
                      <p className="text-center text-[#1C1C1A]/60 py-4">✅ No outstanding balances!</p>
                    )}
                  </div>
                </div>
              )}

            {sales.length === 0 ? (
              <p className="text-center text-[#1C1C1A]/60 py-8">No sales recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#E5E5DF]">
                    <tr>
                      <th className="p-3 text-left w-8"></th>
                      <th className="p-3 text-left">Invoice</th>
                      <th className="p-3 text-left">Customer</th>
                      <th className="p-3 text-right">Items</th>
                      <th className="p-3 text-right">Total</th>
                      <th className="p-3 text-right">Paid</th>
                      <th className="p-3 text-right">Balance</th>
                      <th className="p-3 text-center">Date</th>
                      <th className="p-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((sale) => {
                      const isOldFormat = !sale.invoice_number;
                      
                      // Calculate balance using same logic as Total Balance Due card
                      const saleAmount = sale.total_amount || sale.sale_amount || 0;
                      const paymentHistory = sale.payment_history || [];
                      
                      // Calculate total balance (all payments, no filter for table display)
                      const totalPaid = paymentHistory.reduce((total, p) => total + (p.amount || 0), 0);
                      const balance = Math.max(0, saleAmount - totalPaid);
                      
                      return (
                        <>
                          <tr key={sale.sale_id} className="border-b border-[#E5E5DF] hover:bg-[#F5F5F0]">
                            <td className="p-3">
                              {!isOldFormat && sale.payment_history && sale.payment_history.length > 0 && (
                                <button onClick={() => toggleSalePaymentHistory(sale.sale_id)} className="hover:bg-[#E5E5DF] rounded p-1">
                                  {expandedSale === sale.sale_id ? <CaretUp size={16} /> : <CaretDown size={16} />}
                                </button>
                              )}
                            </td>
                            <td className="p-3">
                              <div className="font-medium">{sale.invoice_number || 'N/A'}</div>
                            </td>
                            <td className="p-3">{sale.customer_name || 'Walk-in'}</td>
                            <td className="p-3 text-right">{sale.line_items?.length || 1}</td>
                            <td className="p-3 text-right font-medium">₹{(sale.total_amount || 0).toLocaleString()}</td>
                            <td className="p-3 text-right text-green-700">
                              ₹{totalPaid.toLocaleString()}
                            </td>
                            <td className="p-3 text-right">
                              <Badge variant={balance > 0 ? 'destructive' : 'success'}>
                                ₹{(balance || 0).toLocaleString()}
                              </Badge>
                            </td>
                            <td className="p-3 text-center text-xs">{sale.sale_date || 'N/A'}</td>
                            <td className="p-3">
                              <div className="flex gap-2 justify-center flex-wrap">
                                <Button size="sm" variant="outline" onClick={() => viewInvoice(sale.sale_id)}>
                                  <Receipt size={16} className="mr-1" />
                                  View
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => openEditSale(sale)} className="text-blue-700 border-blue-700">
                                  <Pencil size={16} className="mr-1" />
                                  Edit
                                </Button>
                                {balance > 0 && (
                                  <Button size="sm" variant="outline" onClick={() => openSalePaymentDialog(sale)} className="text-green-700 border-green-700">
                                    Collect
                                  </Button>
                                )}
                                {balance === 0 && !sale.stock_deducted && (
                                  <Button size="sm" variant="outline" onClick={() => openDeliverDialog(sale)} className="text-purple-700 border-purple-700">
                                    <Package size={16} className="mr-1" />
                                    Deliver
                                  </Button>
                                )}
                                {sale.stock_deducted && (
                                  <Badge variant="success" className="text-xs">
                                    ✓ Delivered
                                  </Badge>
                                )}
                                {user?.role === 'admin' && (
                                  <Button size="sm" variant="outline" onClick={() => openDeleteDialog(sale)} className="text-red-700 border-red-700">
                                    Delete
                                  </Button>
                                )}
                                {isOldFormat && (
                                  <span className="text-xs text-[#1C1C1A]/40 ml-2">Legacy</span>
                                )}
                              </div>
                            </td>
                          </tr>
                          {expandedSale === sale.sale_id && sale.payment_history && (
                            <tr>
                              <td colSpan="9" className="p-3 bg-[#F9F9F8]">
                                <div className="text-xs">
                                  <p className="font-semibold mb-2">Payment History:</p>
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b">
                                        <th className="text-left p-2">Date</th>
                                        <th className="text-left p-2">Amount</th>
                                        <th className="text-left p-2">Method</th>
                                        <th className="text-left p-2">Remarks</th>
                                        <th className="text-left p-2">By</th>
                                        <th className="text-center p-2">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sale.payment_history.map((payment, idx) => (
                                        <tr key={idx} className="border-b">
                                          <td className="p-2">{payment.payment_date}</td>
                                          <td className="p-2">₹{(payment.amount || 0).toLocaleString()}</td>
                                          <td className="p-2">{payment.payment_method?.toUpperCase()}</td>
                                          <td className="p-2">{payment.remarks || '-'}</td>
                                          <td className="p-2">{payment.created_by_name}</td>
                                          <td className="p-2 text-center">
                                            <Button 
                                              size="sm" 
                                              variant="outline" 
                                              onClick={() => openPaymentEdit(sale, payment, idx)}
                                              className="text-blue-700 border-blue-700 text-xs"
                                            >
                                              <Pencil size={12} className="mr-1" />
                                              Edit
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        {/* Sale Dialog - Record New Sale */}
        <Dialog open={showSaleDialog} onOpenChange={(open) => {
          setShowSaleDialog(open);
          if (!open) {
            setIsEditMode(false);
            setEditingSale(null);
            setSaleCart([]);
            setSaleCustomer('');
            setSalePayment({ amount_paid: 0, payment_method: 'cash' });
            setSaleDateEdit(''); // Reset date when closing dialog
          }
        }}>
          <DialogContent className="bg-white max-w-3xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">
                {isEditMode ? `Edit Sale - ${editingSale?.invoice_number}` : 'Record Sale'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {/* Invoice Date - Only show in edit mode */}
              {isEditMode && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Invoice Date</h4>
                  <div>
                    <Label>Sale Date</Label>
                    <Input
                      type="date"
                      value={saleDateEdit}
                      onChange={(e) => setSaleDateEdit(e.target.value)}
                      className="w-full"
                    />
                    <p className="text-xs text-[#1C1C1A]/60 mt-1">
                      Change this to update the invoice date
                    </p>
                  </div>
                </Card>
              )}

              {/* Add Items to Cart */}
              <Card className="p-4 bg-[#F9F9F8]">
                <h4 className="font-semibold mb-3 text-[#2C4C3B]">Add Items to Cart</h4>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label>Barcode</Label>
                    <div className="flex gap-2">
                      <Input
                        value={currentItem.barcode}
                        onChange={(e) => setCurrentItem({...currentItem, barcode: e.target.value})}
                        placeholder="Scan or enter"
                        className="flex-1"
                      />
                      <BarcodeScanButton 
                        onScan={(code) => setCurrentItem({...currentItem, barcode: code})}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      value={currentItem.quantity}
                      onChange={(e) => setCurrentItem({...currentItem, quantity: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label>Price</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={currentItem.price_per_unit}
                      onChange={(e) => setCurrentItem({...currentItem, price_per_unit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                </div>
                <Button type="button" size="sm" onClick={handleAddToCart} className="w-full">
                  <Plus size={16} className="mr-2" />
                  Add to Cart
                </Button>
              </Card>

              {/* Cart Items */}
              {saleCart.length > 0 && (
                <Card className="p-4">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Cart Items ({saleCart.length})</h4>
                  <div className="space-y-2">
                    {saleCart.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-[#F9F9F8] rounded">
                        <div className="flex-1">
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-xs text-[#1C1C1A]/60">
                            {item.quantity} x ₹{(item.price_per_unit || 0).toLocaleString()} = ₹{(item.subtotal || 0).toLocaleString()}
                          </p>
                        </div>
                        <Button type="button" size="sm" variant="ghost" onClick={() => handleRemoveFromCart(index)}>
                          <Trash size={16} className="text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t flex justify-between items-center">
                    <span>Total Amount:</span>
                    <span className="text-[#2C4C3B]">₹{getCartTotal().toLocaleString()}</span>
                  </div>
                </Card>
              )}

              {/* Customer Selection */}
              {saleCart.length > 0 && (
                <Card className="p-4 bg-green-50">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Customer (Optional)</h4>
                  <Select value={saleCustomer} onValueChange={setSaleCustomer}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select customer (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.name} - REG {customer.reg_no}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {saleCustomer && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setSaleCustomer('')}
                      className="mt-2 text-xs"
                    >
                      Clear Selection
                    </Button>
                  )}
                </Card>
              )}

              {/* Payment Section */}
              {saleCart.length > 0 && (
                <Card className="p-4 bg-green-50">
                  <h4 className="font-semibold mb-3 text-[#2C4C3B]">Payment Details</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Amount Paid (Booking Amount)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={salePayment.amount_paid}
                        onChange={(e) => setSalePayment({...salePayment, amount_paid: parseFloat(e.target.value) || 0})}
                        placeholder="Enter amount received"
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <Select value={salePayment.payment_method} onValueChange={(val) => setSalePayment({...salePayment, payment_method: val})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHODS.map(method => (
                            <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="mt-3 p-3 bg-white rounded">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Total Amount:</span>
                      <span className="font-medium">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Amount Paid:</span>
                      <span className="font-medium text-green-700">₹{salePayment.amount_paid.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold pt-2 border-t">
                      <span>Remaining Balance:</span>
                      <span className={`${(getCartTotal() - salePayment.amount_paid) > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                        ₹{(getCartTotal() - salePayment.amount_paid).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              <div className="flex gap-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowSaleDialog(false);
                    setSaleCart([]);
                    setSaleCustomer('');
                    setSalePayment({ amount_paid: 0, payment_method: 'cash' });
                    setSaleDateEdit(''); // Reset date
                  }} 
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleSaleSubmit} 
                  disabled={saleCart.length === 0}
                  className="flex-1 bg-[#2C4C3B]"
                >
                  <Receipt size={20} className="mr-2" />
                  {isEditMode ? 'Update Sale' : 'Record Sale & Generate Invoice'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoice Modal */}
        <Dialog open={showInvoiceModal} onOpenChange={(open) => {
          setShowInvoiceModal(open);
          if (!open) {
            // Refresh sales data when modal closes to sync any updates
            fetchSalesData();
          }
        }}>
          <DialogContent className="bg-white max-w-4xl max-h-[90vh] overflow-y-auto print:max-w-full">
            <DialogHeader className="print:hidden">
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Invoice</DialogTitle>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => {
                  setShowInvoiceModal(false);
                  openEditSale(currentInvoice);
                }} variant="outline" className="text-blue-700 border-blue-700">
                  <Pencil size={16} className="mr-2" />
                  Edit
                </Button>
                <Button size="sm" onClick={printInvoice} variant="outline">
                  <Printer size={16} className="mr-2" />
                  Print
                </Button>
                <Button size="sm" onClick={downloadInvoice} variant="outline">
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
              </div>
            </DialogHeader>
            
            {currentInvoice && (
              <div id="invoice-content" className="p-8 bg-white">
                {/* Invoice Header */}
                <div className="text-center mb-6 pb-4 border-b-2 border-[#2C4C3B]">
                  <h1 className="text-3xl font-bold text-[#2C4C3B]">INVOICE</h1>
                  <div className="text-lg font-semibold mt-2">{currentInvoice.branch_name}</div>
                  <div className="text-sm text-[#1C1C1A]/60 mt-1">Therapy & Wellness Center</div>
                </div>

                {/* Invoice Info */}
                <div className="grid grid-cols-2 gap-8 mb-6">
                  <div>
                    <h3 className="font-semibold text-[#2C4C3B] mb-2">Bill To:</h3>
                    <div className="text-sm">
                      <div className="font-medium">{currentInvoice.customer_name}</div>
                      {currentInvoice.customer_reg_no && <div>REG: {currentInvoice.customer_reg_no}</div>}
                      {currentInvoice.customer_phone && <div>Phone: {currentInvoice.customer_phone}</div>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm">
                      <div className="font-semibold text-lg">{currentInvoice.invoice_number}</div>
                      <div className="mt-2">Date: {currentInvoice.sale_date}</div>
                      <div>Time: {new Date(currentInvoice.created_at).toLocaleTimeString()}</div>
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <table className="w-full mb-6 border-collapse">
                  <thead>
                    <tr className="bg-[#E5E5DF]">
                      <th className="p-3 text-left border">#</th>
                      <th className="p-3 text-left border">Item Description</th>
                      <th className="p-3 text-center border">Quantity</th>
                      <th className="p-3 text-right border">Price</th>
                      <th className="p-3 text-right border">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentInvoice.line_items?.map((item, index) => (
                      <tr key={index}>
                        <td className="p-3 border">{index + 1}</td>
                        <td className="p-3 border">
                          <div className="font-medium">{item.item_name}</div>
                          <div className="text-xs text-[#1C1C1A]/60">Code: {item.barcode}</div>
                        </td>
                        <td className="p-3 text-center border">{item.quantity} {item.unit}</td>
                        <td className="p-3 text-right border">₹{(item.price_per_unit || 0).toLocaleString()}</td>
                        <td className="p-3 text-right border font-medium">₹{(item.subtotal || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end">
                  <div className="w-64">
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-medium">Subtotal:</span>
                      <span>₹{currentInvoice.total_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b">
                      <span className="font-semibold text-lg">Total Amount:</span>
                      <span className="font-bold text-lg text-[#2C4C3B]">₹{currentInvoice.total_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b text-green-700">
                      <span className="font-medium">Amount Paid ({currentInvoice.payment_method?.toUpperCase()}):</span>
                      <span className="font-medium">₹{currentInvoice.amount_paid?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 font-bold">
                      <span>Balance Due:</span>
                      <span className={(currentInvoice.calculated_balance || 0) > 0 ? 'text-orange-700' : 'text-green-700'}>
                        ₹{(currentInvoice.calculated_balance || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 pt-4 border-t text-center text-sm text-[#1C1C1A]/60">
                  <p>Thank you for your business!</p>
                  <p className="mt-2">Recorded by: {currentInvoice.created_by_name}</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sale Payment Dialog (Collect Balance) */}
        <Dialog open={showSalePaymentDialog} onOpenChange={(open) => {
          setShowSalePaymentDialog(open);
          if (!open && selectedSale) {
            // Refresh sales data when dialog closes
            fetchSalesData();
          }
        }}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Collect Balance Payment</DialogTitle>
              {selectedSale && (
                <div className="mt-2 text-sm">
                  <p className="text-[#1C1C1A]/70">Invoice: {selectedSale.invoice_number}</p>
                  <p className="text-[#1C1C1A]/70">Customer: {selectedSale.customer_name}</p>
                  <p className="text-orange-700 font-medium">Balance Due: ₹{calculatedBalance.toLocaleString()}</p>
                </div>
              )}
            </DialogHeader>
            <form onSubmit={handleSalePaymentSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Amount *</Label>
                  <Input
                    type="number"
                    required
                    min="0.01"
                    step="0.01"
                    max={calculatedBalance}
                    value={salePaymentForm.amount}
                    onChange={(e) => setSalePaymentForm({...salePaymentForm, amount: parseFloat(e.target.value)})}
                    placeholder="Enter amount"
                    autoFocus
                  />
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select 
                    value={salePaymentForm.payment_method} 
                    onValueChange={(val) => setSalePaymentForm({...salePaymentForm, payment_method: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method.value} value={method.value}>{method.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Remarks</Label>
                <Input
                  value={salePaymentForm.remarks}
                  onChange={(e) => setSalePaymentForm({...salePaymentForm, remarks: e.target.value})}
                  placeholder="Payment notes (optional)"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowSalePaymentDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Collect Payment
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Payment Edit Dialog */}
        <Dialog open={showPaymentEditDialog} onOpenChange={setShowPaymentEditDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-[#2C4C3B]">
                Edit Payment - {editingPayment?.invoice_number}
              </DialogTitle>
            </DialogHeader>
            
            {editingPayment && (
              <form onSubmit={handlePaymentEdit} className="space-y-4">
                <div>
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={editingPayment.payment_date}
                    onChange={(e) => setEditingPayment({...editingPayment, payment_date: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label>Amount (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingPayment.amount}
                    onChange={(e) => setEditingPayment({...editingPayment, amount: parseFloat(e.target.value)})}
                    required
                  />
                </div>
                
                <div>
                  <Label>Payment Method</Label>
                  <select
                    value={editingPayment.payment_method}
                    onChange={(e) => setEditingPayment({...editingPayment, payment_method: e.target.value})}
                    className="w-full p-2 border border-[#1C1C1A]/20 rounded"
                    required
                  >
                    <option value="cash">Cash</option>
                    <option value="online">Online</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                  </select>
                </div>
                
                <div>
                  <Label>Remarks</Label>
                  <Input
                    type="text"
                    value={editingPayment.remarks}
                    onChange={(e) => setEditingPayment({...editingPayment, remarks: e.target.value})}
                    placeholder="Optional remarks"
                  />
                </div>
                
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowPaymentEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#2C4C3B]">
                    Update Payment
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-red-700">Delete Sale Record</DialogTitle>
            </DialogHeader>
            {saleToDelete && (
              <div className="space-y-4">
                <p className="text-[#1C1C1A]/80">
                  Are you sure you want to delete this sale?
                </p>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm font-medium">Invoice: {saleToDelete.invoice_number}</p>
                  <p className="text-sm">Customer: {saleToDelete.customer_name}</p>
                  <p className="text-sm">Amount: ₹{(saleToDelete.total_amount || saleToDelete.sale_amount || 0).toLocaleString()}</p>
                  <p className="text-sm">Date: {saleToDelete.sale_date}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-900">
                    ℹ️ {saleToDelete.stock_deducted 
                      ? 'Stock will be restored to inventory after deletion.' 
                      : 'No stock was deducted for this sale.'}
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeleteDialog(false);
                      setSaleToDelete(null);
                    }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeleteSale} 
                    className="flex-1 bg-red-700 hover:bg-red-800"
                  >
                    Delete Sale
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Deliver Confirmation Dialog */}
        <Dialog open={showDeliverDialog} onOpenChange={setShowDeliverDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-purple-700">Mark as Delivered</DialogTitle>
            </DialogHeader>
            {saleToDeliver && (
              <div className="space-y-4">
                <p className="text-[#1C1C1A]/80">
                  Are you sure you want to mark this sale as delivered? This will deduct the stock from inventory.
                </p>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <p className="text-sm font-medium">Invoice: {saleToDeliver.invoice_number}</p>
                  <p className="text-sm">Customer: {saleToDeliver.customer_name}</p>
                  <p className="text-sm">Amount: ₹{(saleToDeliver.total_amount || saleToDeliver.sale_amount || 0).toLocaleString()}</p>
                  <p className="text-sm">Date: {saleToDeliver.sale_date}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-900">
                    ℹ️ Stock will be deducted for all items in this sale and a delivery record will be created.
                  </p>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowDeliverDialog(false);
                      setSaleToDeliver(null);
                    }} 
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleDeliverSale} 
                    className="flex-1 bg-purple-700 hover:bg-purple-800"
                  >
                    Confirm Delivery
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default SalesRecords;
